import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useMSSByAccountBySymbol } from '@src/providers/MarketSecurityStatusProvider';
import {
  ConnectionType,
  useConnectionStatusContext,
  useSecuritiesContext,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import {
  getMarketAccountActiveCondition,
  type GetMarketAccountActiveConditionParams,
} from '../conditions/getMarketAccountActiveCondition';
import {
  getMarketConfiguredCondition,
  type GetMarketConfiguredConditionParams,
} from '../conditions/useMarketConfiguredCondition';
import {
  getMSSCapabilityEnabledCondition,
  type GetMSSCapabilityEnabledConditionParams,
} from '../conditions/useMSSCapabilityEnabledCondition';
import { getMSSEnabledCondition, type GetMSSEnabledConditionParams } from '../conditions/useMSSEnabledCondition';

interface UseMarketDataMarketSelectorConditionsParams {
  symbol: string | undefined;
  /** When using unified liquidity, pass the token list so an account stays selectable if any UL symbol is tradable */
  ulTokensForSymbol?: UnifiedLiquidityToken[];
  /** Whether or not to query for MSS records at all. Defaults to true. */
  queryMSSRecords?: boolean;
}

/**
 * Collects the availability conditions applicable to the Market Data use case of the Market Selector.
 */
export const useMarketDataMarketSelectorConditions = ({
  symbol,
  ulTokensForSymbol,
  queryMSSRecords = true,
}: UseMarketDataMarketSelectorConditionsParams) => {
  const { useNewMarketSelector } = useTradingSettings();
  const { enableNewMarketSelectorMSSValidation } = useFeatureFlag();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketCredentialsWithConnectionStatusByMarketAccountName } = useConnectionStatusContext();
  const enableMSSValidation = useNewMarketSelector && enableNewMarketSelectorMSSValidation && queryMSSRecords;

  const symbols = useMemo(
    () => compact(ulTokensForSymbol ? [symbol, ...ulTokensForSymbol.map(token => token.Symbol)] : [symbol]),
    [ulTokensForSymbol, symbol]
  );

  const mssByAccountBySymbol = useMSSByAccountBySymbol({
    symbols,
    tag: `MarketSelector: Market Data MSS Conditions`,
    makeQuery: queryMSSRecords,
  });

  const conditions = useMemo(
    () =>
      getMarketDataMarketSelectorConditions({
        enableMSSValidation,
        symbol,
        ulTokensForSymbol,
        mssByAccountBySymbol,
        marketCredentialsWithConnectionStatusByMarketAccountName,
        securitiesBySymbol,
      }),
    [
      enableMSSValidation,
      marketCredentialsWithConnectionStatusByMarketAccountName,
      mssByAccountBySymbol,
      securitiesBySymbol,
      symbol,
      ulTokensForSymbol,
    ]
  );

  return { conditions, allConditionsReady: conditions.every(c => c.ready) };
};

export type GetMarketDataMarketSelectorConditionsParams = {
  /** Whether or not to perform MSS validation */
  enableMSSValidation: boolean;
} & Omit<GetMarketAccountActiveConditionParams, 'disableIfTradingDisabled'> &
  Omit<GetMarketConfiguredConditionParams, 'connectionType'> &
  Omit<GetMSSCapabilityEnabledConditionParams & GetMSSEnabledConditionParams, 'capability' | 'connectionType'>;

export function getMarketDataMarketSelectorConditions({
  enableMSSValidation,
  symbol,
  ulTokensForSymbol,
  marketCredentialsWithConnectionStatusByMarketAccountName,
  mssByAccountBySymbol,
  securitiesBySymbol,
}: GetMarketDataMarketSelectorConditionsParams) {
  const marketAccountActiveCondition = getMarketAccountActiveCondition({
    marketCredentialsWithConnectionStatusByMarketAccountName,
    // We allow you to select market accounts that have trading disabled when your intention is to view market data.
    disableIfTradingDisabled: false,
  });
  const marketConfiguredCondition = getMarketConfiguredCondition({ connectionType: ConnectionType.MarketData });

  const mssEnabledCondition = getMSSEnabledCondition({
    connectionType: ConnectionType.MarketData,
    securitiesBySymbol,
    mssByAccountBySymbol,
    symbol,
    ulTokensForSymbol,
    justWarnOnFailure: true,
  });
  const mssMarketDataEnabledCondition = getMSSCapabilityEnabledCondition({
    connectionType: ConnectionType.MarketData,
    securitiesBySymbol,
    capability: 'MarketData',
    mssByAccountBySymbol,
    symbol,
    ulTokensForSymbol,
    justWarnOnFailure: true,
  });

  return compact([
    marketConfiguredCondition,
    marketAccountActiveCondition,

    enableMSSValidation && mssMarketDataEnabledCondition,
    enableMSSValidation && mssEnabledCondition,
  ]);
}
